import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, BarChart3, Globe2, Activity, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  LineChart, Line, CartesianGrid, Legend, PieChart, Pie, Cell,
} from "recharts";

const COLORS = ["#10B981", "#0EA5E9", "#8B5CF6", "#F59E0B", "#EF4444", "#EC4899", "#7C3AED", "#DC2626"];

const TYPE_LABELS = {
  bmi: "ИТМ", "vitamin_d": "Витамин D", "water_intake": "Вода",
  period: "Цикъл", egfr: "eGFR", "homa_ir": "HOMA-IR",
  "cvd_risk": "ССЗ риск", "diabetes_risk": "Диабет",
  breast_cancer: "Рак на гърдата", dental: "Дентален",
};

export default function RiskStatsPanel() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [distribution, setDistribution] = useState({});
  const [timeseries, setTimeseries] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d, t, h, r] = await Promise.all([
        axios.get(`${API}/admin/risk-stats/summary`, { params: { days } }),
        axios.get(`${API}/admin/risk-stats/distribution`, { params: { days } }),
        axios.get(`${API}/admin/risk-stats/timeseries`, { params: { days } }),
        axios.get(`${API}/admin/risk-stats/heatmap`, { params: { days } }),
        axios.get(`${API}/admin/risk-stats/recent`, { params: { limit: 30 } }),
      ]);
      setSummary(s.data);
      setDistribution(d.data.distribution || {});
      setTimeseries(t.data.rows || []);
      setHeatmap(h.data);
      setRecent(r.data.items || []);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error("risk-stats load error", e);
    } finally {
      setLoading(false);
    }
  }, [API, days]);

  useEffect(() => { load(); }, [load]);

  const cardCls = isDark
    ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,20%)]"
    : "bg-white border-gray-100";
  const txt = isDark ? "text-white" : "text-[#1A1A2E]";

  // Pivot timeseries → days × types matrix for line chart
  const pivot = {};
  timeseries.forEach(({ day, calculator_type, count }) => {
    if (!pivot[day]) pivot[day] = { day };
    pivot[day][calculator_type] = count;
  });
  const lineData = Object.values(pivot).sort((a, b) => a.day.localeCompare(b.day));
  const calcTypesInTs = [...new Set(timeseries.map((r) => r.calculator_type))];

  const heatmapMax = heatmap?.grid ? Math.max(...heatmap.grid.flat(), 1) : 1;

  return (
    <div data-testid="risk-stats-panel" className="space-y-5">
      {/* Header + filter */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 className={`w-5 h-5 ${isDark ? "text-violet-400" : "text-violet-500"}`} />
          <h2 className={`text-lg font-bold ${txt}`}>Risk Stats</h2>
        </div>
        <div className="flex items-center gap-1.5">
          {[7, 30, 90, 365].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-semibold ${
                days === d
                  ? "bg-violet-500 text-white"
                  : isDark ? "bg-gray-800 text-gray-400 hover:text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              data-testid={`risk-days-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading && !summary ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
        </div>
      ) : (
        <>
          {/* Summary cards */}
          {summary && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="risk-summary">
              <SummaryCard isDark={isDark} icon={Activity} label="Общо оценки" value={summary.total} color="#10B981" />
              <SummaryCard isDark={isDark} icon={Globe2} label="Анонимни" value={summary.anonymous} color="#0EA5E9" />
              <SummaryCard isDark={isDark} icon={TrendingUp} label="Регистрирани" value={summary.logged_in} color="#8B5CF6" />
              <SummaryCard isDark={isDark} icon={TrendingUp} label="Anon → Auth %" value={`${summary.anon_to_auth_rate_pct}%`} color="#F59E0B" />
            </div>
          )}

          {/* Distribution by calculator type (bar chart) */}
          {summary && summary.by_type?.length > 0 && (
            <Card className={cardCls}>
              <CardContent className="p-4">
                <h3 className={`text-sm font-bold mb-3 ${txt}`}>Брой оценки по тип калкулатор</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={summary.by_type.map((t) => ({
                    name: TYPE_LABELS[t.calculator_type] || t.calculator_type,
                    count: t.count,
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1f2937" : "#e5e7eb"} />
                    <XAxis dataKey="name" stroke={isDark ? "#9ca3af" : "#4b5563"} fontSize={11} />
                    <YAxis stroke={isDark ? "#9ca3af" : "#4b5563"} fontSize={11} />
                    <Tooltip contentStyle={{ background: isDark ? "#1f2937" : "#fff", border: "none" }} />
                    <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Time series */}
          {lineData.length > 0 && (
            <Card className={cardCls}>
              <CardContent className="p-4">
                <h3 className={`text-sm font-bold mb-3 ${txt}`}>Дневен брой по тип</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={lineData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1f2937" : "#e5e7eb"} />
                    <XAxis dataKey="day" stroke={isDark ? "#9ca3af" : "#4b5563"} fontSize={10} />
                    <YAxis stroke={isDark ? "#9ca3af" : "#4b5563"} fontSize={11} />
                    <Tooltip contentStyle={{ background: isDark ? "#1f2937" : "#fff", border: "none" }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {calcTypesInTs.map((t, i) => (
                      <Line key={t} type="monotone" dataKey={t} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Distribution per band (pie) */}
          {Object.keys(distribution).length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(distribution).slice(0, 4).map(([calc, bands]) => (
                <Card key={calc} className={cardCls}>
                  <CardContent className="p-4">
                    <h4 className={`text-xs font-bold mb-2 uppercase ${txt}`}>
                      {TYPE_LABELS[calc] || calc}
                    </h4>
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie data={bands} dataKey="count" nameKey="band" cx="50%" cy="50%" outerRadius={60}
                             label={(entry) => `${entry.band}: ${entry.count}`} labelLine={false} fontSize={10}>
                          {bands.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ background: isDark ? "#1f2937" : "#fff", border: "none", fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Heatmap hour × day-of-week */}
          {heatmap?.grid && (
            <Card className={cardCls}>
              <CardContent className="p-4">
                <h3 className={`text-sm font-bold mb-3 ${txt}`}>Час × Ден от седмицата</h3>
                <div className="overflow-x-auto">
                  <table className="text-[10px] tabular-nums" data-testid="risk-heatmap">
                    <thead>
                      <tr>
                        <th className="px-1 py-1"></th>
                        {Array.from({ length: 24 }).map((_, h) => (
                          <th key={h} className={`px-1 py-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {heatmap.grid.map((row, dowIdx) => (
                        <tr key={dowIdx}>
                          <td className={`px-2 py-1 font-bold ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                            {heatmap.labels[dowIdx]}
                          </td>
                          {row.map((cell, hIdx) => {
                            const intensity = cell / heatmapMax;
                            const bg = `rgba(139, 92, 246, ${0.05 + intensity * 0.85})`;
                            return (
                              <td
                                key={hIdx}
                                title={`${heatmap.labels[dowIdx]} ${hIdx}:00 = ${cell}`}
                                className="px-1.5 py-1 text-center"
                                style={{ backgroundColor: bg, color: intensity > 0.5 ? "#fff" : isDark ? "#cbd5e1" : "#1f2937" }}
                              >
                                {cell || ""}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent live feed */}
          {recent.length > 0 && (
            <Card className={cardCls}>
              <CardContent className="p-4">
                <h3 className={`text-sm font-bold mb-3 ${txt}`}>Последни 30 оценки</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]" data-testid="risk-recent">
                    <thead>
                      <tr className={`text-left ${isDark ? "text-gray-400" : "text-gray-500"} text-[10px] uppercase`}>
                        <th className="py-1">Тип</th>
                        <th className="py-1">Резултат</th>
                        <th className="py-1">Сесия</th>
                        <th className="py-1">Lang</th>
                        <th className="py-1">Време</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.map((r, i) => (
                        <tr key={i} className={isDark ? "border-t border-[hsl(223,47%,18%)]" : "border-t border-gray-100"}>
                          <td className="py-1.5">{TYPE_LABELS[r.calculator_type] || r.calculator_type}</td>
                          <td className="py-1.5">{r.score_label || "—"}</td>
                          <td className="py-1.5 font-mono text-[10px]">{r.user_id ? "user" : r.session_id}</td>
                          <td className="py-1.5">{r.language}</td>
                          <td className={`py-1.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                            {r.created_at ? new Date(r.created_at).toLocaleString("bg") : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, color, isDark }) {
  return (
    <div
      className={`rounded-xl p-3 border ${isDark ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,20%)]" : "bg-white border-gray-100"}`}
    >
      <div className="flex items-start justify-between mb-2">
        <p className={`text-[10px] uppercase font-bold ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          {label}
        </p>
        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: color + "22" }}>
          <Icon className="w-3.5 h-3.5" style={{ color }} />
        </div>
      </div>
      <p className={`text-2xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{value}</p>
    </div>
  );
}
